/**
 * 出力画像フォーマットと背景透過の判定。
 *
 * useGenerationState.tsx から切り出したもの (ロジック変更なし)。
 */

/** 出力画像フォーマット */
export type ImageOutputFormat = 'png' | 'jpeg' | 'webp';

/** 利用可能な全出力フォーマット (UI の型ガード用。実際の選択肢は DB から取得) */
export const ALL_IMAGE_OUTPUT_FORMATS: ImageOutputFormat[] = ['png', 'jpeg', 'webp'];

/** 背景指定。'transparent' は GPT Image + PNG/WebP 限定。未指定は API 既定 (auto) */
export type ImageBackgroundMode = 'transparent' | 'opaque' | 'auto';

/** background='transparent' が使える出力形式 (アルファチャンネル対応) */
export const TRANSPARENCY_CAPABLE_FORMATS: readonly ImageOutputFormat[] = ['png', 'webp'];

/** モデルが background='transparent' をサポートするか。
 * gpt-image-1 系は従来から対応。gpt-image-2 も 2026-08-20 からプレビュー対応
 * (gpt-image-2.5 Flare / Sunburst も同じ Images API パラメータを受ける)
 * (https://developers.openai.com/api/docs/guides/image-generation)。
 */
export function isTransparencyCapableModel(modelCode: string | undefined): boolean {
  if (!modelCode) return false;
  return modelCode.startsWith('gpt-image-');
}

/** モデル×フォーマットが背景透過をサポートするか */
export function supportsTransparency(
  modelCode: string | undefined,
  format: ImageOutputFormat | undefined,
): boolean {
  if (!isTransparencyCapableModel(modelCode)) return false;
  if (!format) return false;
  return TRANSPARENCY_CAPABLE_FORMATS.includes(format);
}

/** DB の generation_models.supported_output_formats を ImageOutputFormat[] に正規化 */
export function normalizeOutputFormats(
  raw: string[] | null | undefined,
): ImageOutputFormat[] {
  if (!raw || raw.length === 0) return ['png'];
  const allowed = new Set<string>(ALL_IMAGE_OUTPUT_FORMATS);
  const normalized = raw
    .map((v) => (v === 'jpg' ? 'jpeg' : v))
    .filter((v): v is ImageOutputFormat => allowed.has(v));
  return normalized.length > 0 ? normalized : ['png'];
}
